'use client';

import { c as _c } from "react/compiler-runtime";
import './slot-card.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Dialog, DialogActions, DialogClose, DialogContent, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { SelectableCard } from '../selectable-card/selectable-card.tsx';
import { canToggleContact, getDialogCapacityState, getDialogContactState, getSaveableContactIds, toggleDraftContact } from './slot-card.logic.ts';
import type { SlotCardAlertType, SlotCardAssignedContact, SlotCardContact } from './slot-card.logic.ts';
export interface SlotCardProps {
  timeDisplay: string;
  label: string;
  remaining: number;
  total: number;
  assignedContacts: SlotCardAssignedContact[];
  disabled: boolean;
  contacts: SlotCardContact[];
  variant?: SlotCardAlertType;
  /** Called with the full set of assigned contact IDs when the user confirms. */
  onSave: (assignedContactIds: string[]) => void;
}
const SlotAssignDialog: React.FC<{
  label: string;
  timeDisplay: string;
  remaining: number;
  total: number;
  contacts: SlotCardContact[];
  onSave: (assignedContactIds: string[]) => void;
  onClose: () => void;
}> = t0 => {
  const $ = _c(48);
  const {
    label,
    timeDisplay,
    remaining,
    total,
    contacts,
    onSave,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== contacts) {
    t2 = () => new Set(contacts.filter(_temp).map(_temp2));
    $[1] = contacts;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [draft, setDraft] = useState(t2);
  let T0;
  let handleDone;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[3] !== contacts || $[4] !== draft || $[5] !== label || $[6] !== onClose || $[7] !== onSave || $[8] !== remaining || $[9] !== t || $[10] !== timeDisplay || $[11] !== total) {
    const {
      lockedIds,
      totalAvailableForNew,
      newSelectionCount,
      isAtCapacity
    } = getDialogCapacityState(contacts, remaining, draft);
    const toggle = id => {
      const contact = contacts.find(c_1 => c_1.id === id);
      if (!contact || !canToggleContact(contact)) {
        return;
      }
      setDraft(prev => toggleDraftContact(id, prev, lockedIds, totalAvailableForNew));
    };
    handleDone = () => {
      onSave(getSaveableContactIds(draft, contacts, lockedIds));
      onClose();
    };
    T0 = DialogContent;
    if ($[19] !== t) {
      t5 = t("volunteers:registration.assignPeopleDescription");
      $[19] = t;
      $[20] = t5;
    } else {
      t5 = $[20];
    }
    const t8 = Math.max(totalAvailableForNew - newSelectionCount, 0);
    let t9;
    if ($[21] !== t || $[22] !== t8 || $[23] !== total) {
      t9 = t("volunteers:slotsRemainingBadge", {
        openCount: t8,
        totalCount: total
      });
      $[21] = t;
      $[22] = t8;
      $[23] = total;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t6 = `${label} • ${timeDisplay} • ${t9}`;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ArkIcon iconName="users-01" size="24" />;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    t3 = "SlotCard__dialog-list flex flex-col gap-snug-lg p-none m-none";
    t4 = contacts.map(contact_0 => {
      const {
        checked,
        isDisabled
      } = getDialogContactState(contact_0, draft, isAtCapacity);
      const isAgeRestricted = contact_0.disabledReason === "ageRestriction";
      const disabledReasonConfig = {
        ageRestriction: {
          className: "body-sm text-neutral-500",
          label: t("volunteers:registration.ageRestriction")
        },
        conflict: {
          className: "flex items-center gap-snug-md pt-snug-md label-moderate label-md label-neutral-mild",
          label: t("volunteers:registration.shiftTimeConflict")
        },
        previouslyRegistered: {
          className: "flex items-center gap-snug-md pt-snug-md label-moderate label-md label-neutral-mild",
          label: t("volunteers:registration.previouslyRegisteredShift")
        },
        sameDayConflict: {
          className: "flex items-center gap-snug-md pt-snug-md label-moderate label-md label-neutral-mild",
          label: t("volunteers:registration.sameDayRegisteredShift")
        }
      } as const;
      return <li key={contact_0.id}><SelectableCard checked={checked} disabled={isDisabled} onChange={() => toggle(contact_0.id)} checkIcon={<ArkIcon iconName="check" color="currentColor" size="20" />}><span className="flex flex-col flex-1 min-w-0"><span className={`label-potent label-lg SlotCard__dialog-name ${checked ? "SlotCard__dialog-name--selected" : ""} ${isDisabled ? "SlotCard__dialog-name--disabled" : ""}`}>{contact_0.displayName}</span>{contact_0.email && <span className="label-lg label-mild label-neutral-moderate SlotCard__dialog-email">{contact_0.email}</span>}{contact_0.disabledReason && contact_0.disabledReason !== "full" && <span className={disabledReasonConfig[contact_0.disabledReason].className}>{!isAgeRestricted && <ArkIcon iconName="info-circle" size="16" className="SlotCard__dialog-info-icon shrink-0" />}{disabledReasonConfig[contact_0.disabledReason].label}</span>}{contact_0.disabledReason === "full" && <span className="flex items-center gap-snug-md pt-snug-md label-moderate label-md label-neutral-mild"><ArkIcon iconName="info-circle" size="16" className="SlotCard__dialog-info-icon shrink-0" />{t("volunteers:registration.slotFull")}</span>}</span></SelectableCard></li>;
    });
    $[3] = contacts;
    $[4] = draft;
    $[5] = label;
    $[6] = onClose;
    $[7] = onSave;
    $[8] = remaining;
    $[9] = t;
    $[10] = timeDisplay;
    $[11] = total;
    $[12] = T0;
    $[13] = handleDone;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    T0 = $[12];
    handleDone = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
  }
  let t8;
  if ($[26] !== t3 || $[27] !== t4) {
    t8 = <ul className={t3}>{t4}</ul>;
    $[26] = t3;
    $[27] = t4;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t) {
    t9 = t("core:actions.cancel");
    $[29] = t;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t9) {
    t10 = <span className="flex-1"><ButtonStyle size="lg" fullWidth={true} purpose="neutral" variant="secondary"><DialogClose>{t9}</DialogClose></ButtonStyle></span>;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t) {
    t11 = t("volunteers:registration.saveChanges");
    $[33] = t;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== handleDone || $[36] !== t11) {
    t12 = <span className="flex-1"><ButtonStyle size="lg" fullWidth={true}><button type="button" onClick={handleDone}>{t11}</button></ButtonStyle></span>;
    $[35] = handleDone;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t10 || $[39] !== t12) {
    t13 = <DialogActions>{t10}{t12}</DialogActions>;
    $[38] = t10;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== T0 || $[42] !== t13 || $[43] !== t5 || $[44] !== t6 || $[45] !== t7 || $[46] !== t8) {
    t14 = <T0 title={t5} supportingText={t6} customIcon={t7}>{t8}{t13}</T0>;
    $[41] = T0;
    $[42] = t13;
    $[43] = t5;
    $[44] = t6;
    $[45] = t7;
    $[46] = t8;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};

/** Slot row with capacity badge. Opens assignment dialog for multi-contact, auto-assigns for single. */
export const SlotCard: React.FC<SlotCardProps> = t0 => {
  const $ = _c(55);
  const {
    timeDisplay,
    label,
    remaining,
    total,
    assignedContacts,
    disabled,
    contacts,
    variant,
    onSave
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const [dialogOpen, setDialogOpen] = useState(false);
  let t2;
  if ($[1] !== contacts) {
    t2 = contacts.filter(_temp3);
    $[1] = contacts;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const eligibleContacts = t2;
  const shouldOpenDialog = contacts.length > 1;
  let t3;
  if ($[3] !== contacts.length || $[4] !== disabled || $[5] !== eligibleContacts || $[6] !== onSave) {
    t3 = () => {
      if (disabled) {
        return;
      }
      if (contacts.length === 1 && eligibleContacts.length === 1) {
        const contact = eligibleContacts[0];
        if (contact.isPreviouslyRegistered) {
          return;
        }
        const nextIds = contact.isAssigned ? [] : [contact.id];
        onSave(nextIds);
        return;
      }
      setDialogOpen(true);
    };
    $[3] = contacts.length;
    $[4] = disabled;
    $[5] = eligibleContacts;
    $[6] = onSave;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleClick = t3;
  let t4;
  if ($[8] !== handleClick || $[9] !== shouldOpenDialog) {
    t4 = shouldOpenDialog ? undefined : e => {
      e.preventDefault();
      handleClick();
    };
    $[8] = handleClick;
    $[9] = shouldOpenDialog;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `SlotCard__button ${variant ? `SlotCard__button--${variant}` : ""} flex p-comfy-sm justify-between items-start rounded-md w-full text-left ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`;
  let t6;
  if ($[11] !== timeDisplay) {
    t6 = <span className="label-lg">{timeDisplay}</span>;
    $[11] = timeDisplay;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `label-md ${disabled ? "SlotCard__remaining--empty" : "label-neutral-mild"}`;
  const t8 = Math.max(remaining, 0);
  let t9;
  if ($[13] !== t || $[14] !== t8 || $[15] !== total) {
    t9 = t("volunteers:registration.slotsAvailable", {
      remaining: t8,
      total
    });
    $[13] = t;
    $[14] = t8;
    $[15] = total;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t9) {
    t10 = <span className={t7}>{t9}</span>;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== assignedContacts) {
    t11 = assignedContacts.length > 0 && <span className="SlotCard__assigned-names flex flex-row flex-wrap items-center gap-snug-md label-potent label-md label-neutral-moderate mt-comfy-sm"><ArkIcon iconName="users-check" size="20" />{assignedContacts.map(_temp4)}</span>;
    $[20] = assignedContacts;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t6) {
    t12 = <span className="flex flex-col flex-1">{t6}{t10}{t11}</span>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t6;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const t13 = `inline-flex items-center gap-snug-md px-snug-xl py-comfy-xs label-potent label-sm shrink-0 ${assignedContacts.length > 0 ? "SlotCard__action--edit" : ""} ${disabled ? "SlotCard__action--disabled" : "SlotCard__action--add"}`;
  const t14 = assignedContacts.length > 0 ? "users-edit" : "user-plus-02";
  let t15;
  if ($[26] !== t14) {
    t15 = <ArkIcon iconName={t14} size="18" />;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== assignedContacts.length || $[29] !== t) {
    t16 = assignedContacts.length > 0 ? t("volunteers:registration.edit") : t("volunteers:registration.add");
    $[28] = assignedContacts.length;
    $[29] = t;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16) {
    t17 = <span className="ps-snug-sm">{t16}</span>;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t13 || $[34] !== t15 || $[35] !== t17) {
    t18 = <span className={t13}>{t15}{t17}</span>;
    $[33] = t13;
    $[34] = t15;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== disabled || $[38] !== t12 || $[39] !== t18 || $[40] !== t4 || $[41] !== t5) {
    t19 = <DialogTrigger><button type="button" disabled={disabled} onClick={t4} className={t5}>{t12}{t18}</button></DialogTrigger>;
    $[37] = disabled;
    $[38] = t12;
    $[39] = t18;
    $[40] = t4;
    $[41] = t5;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== contacts || $[44] !== dialogOpen || $[45] !== label || $[46] !== onSave || $[47] !== remaining || $[48] !== timeDisplay || $[49] !== total) {
    t20 = dialogOpen && <SlotAssignDialog label={label} timeDisplay={timeDisplay} remaining={remaining} total={total} contacts={contacts} onSave={onSave} onClose={() => setDialogOpen(false)} />;
    $[43] = contacts;
    $[44] = dialogOpen;
    $[45] = label;
    $[46] = onSave;
    $[47] = remaining;
    $[48] = timeDisplay;
    $[49] = total;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== dialogOpen || $[52] !== t19 || $[53] !== t20) {
    t21 = <li><Dialog open={dialogOpen} onOpenChange={setDialogOpen}>{t19}{t20}</Dialog></li>;
    $[51] = dialogOpen;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  return t21;
};
function _temp(c) {
  return c.isAssigned;
}
function _temp2(c_0) {
  return c_0.id;
}
function _temp3(c) {
  return c.isAssigned || !c.isDisabled;
}
function _temp4(ac) {
  return <span key={ac.name} className={`SlotCard__name-chip inline-flex items-center gap-snug-md py-snug-md px-snug-xl rounded-sm ${ac.isPreviouslyRegistered ? "SlotCard__name-chip--locked" : ""}`}>{ac.name}{ac.isPreviouslyRegistered ? <ArkIcon iconName="lock-01" size="14" /> : <ArkIcon iconName="x-close" size="14" />}</span>;
}